<template>
  <ProfileMetrics
    v-if="showLegacyMetrics"
    :data="
      data?.fields
        ?.profileV1Metrics as TypeProfileMetricsWithoutUnresolvableLinksResponse
    "
    current-store-only
    hide-store-info
  />
  <div
    v-else
    :class="[$style.config, $style.wrap]"
    :style="data?.fields?.cssOverride as StyleValue"
  >
    <div :class="$style.inner">
      <div :class="$style.header">
        <div :class="$style.headingWrap">
          <BaseHeading size="h6" as="h2">
            {{ data?.fields?.header }}
          </BaseHeading>
        </div>
        <div :class="$style.meta">
          <MarkedParser
            :md="data?.fields?.subheader"
            :class="$style.metaCopy"
          />
        </div>
      </div>
      <div :class="$style.contentWrap">
        <div
          :class="[$style.content, missingData && $style.missingData]"
          :inert="missingData"
          :aria-hidden="missingData ? 'true' : undefined"
        >
          <div v-if="!isGamestopStore" :class="$style.copyWrap">
            <MarkedParser
              :md="computedCopy"
              :class="['u-fs-p1', $style.copy]"
            />
          </div>
          <!-- <div :class="$style.tiers">
            <div
              v-for="tier in Array.from(Array(maxTier + 1).keys())"
              :key="tier"
              :class="[$style.tierWrap, tier !== 0 && $style.grow]"
              :style="{
                '--md-tier-progress-percentage':
                  formatPercentage(rollingTier % 1) || '0%',
              }"
            >
              <div
                v-if="tier !== 0"
                :class="[
                  $style.divider,
                  tier === Math.floor(rollingTier) + 1 && $style.active,
                  tier > Math.floor(rollingTier) + 1 && $style.locked,
                ]"
              />
              <div
                :class="[
                  $style.tier,
                  // Highlight current tier, instead of rolling tier
                  tier === Math.floor(currentTier) && $style.active,
                  tier > Math.floor(currentTier) && $style.locked,
                ]"
              >
                {{ tier || 'E' }}
              </div>
            </div>
          </div> -->
          <div :class="$style.categories">
            <div
              v-for="category in computedCategories"
              :key="category.id"
              :class="$style.categoryWrap"
              @click="selectCategory(category.id)"
            >
              <div
                :class="[
                  $style.category,
                  selectedCategory === category.id && $style.active,
                  category.id === 'customer-experience' && $style.singleColumn,
                ]"
              >
                <div :class="$style.categoryHeader">
                  <h3 :class="$style.categoryHeading">
                    {{ category.name }}
                  </h3>
                  <div
                    v-if="category.status"
                    :class="[$style.tag, $style[category.status]]"
                  >
                    {{ localizedStrings[category.status] }}
                  </div>
                </div>
                <div :class="$style.metrics">
                  <div
                    v-for="metric in category.metrics"
                    :key="metric.id"
                    :class="$style.metric"
                  >
                    <div :class="$style.metricLabelWrap">
                      <div
                        v-if="metric.status"
                        :class="[$style.tag, $style[metric.status]]"
                      >
                        {{ localizedStrings[metric.status] }}
                      </div>
                      <NuxtLinkLocale
                        v-if="metric.url"
                        :to="metric.url"
                        target="_blank"
                        :class="[$style.metricLabel, $style.url]"
                        @click.stop="
                          pushToGaDataLayer({
                            event: 'ga4.trackEvent',
                            eventName: 'metric_info_click',
                            eventParams: {
                              ...defaultEventParams,
                              metric_name: metric?.id,
                              metric_category: category.id,
                              click_location: 'category section',
                              link_url: metric.url,
                              outbound: metric.url.includes('wpn.wizards.com')
                                ? false
                                : true,
                            },
                          })
                        "
                      >
                        {{ metric.label }}
                        <IconInfo width="16" height="16" />
                      </NuxtLinkLocale>
                      <span v-else :class="$style.metricLabel">
                        {{ metric.label }}
                      </span>
                      <span :class="$style.metricTooltip">
                        {{ metric.tooltip }}
                      </span>
                    </div>
                    <div :class="$style.metricValueWrap">
                      <span :class="$style.metricValue">{{
                        metric.type === 'percent'
                          ? formatPercentage(metric.value) || '0%'
                          : metric.value
                      }}</span>
                      <div
                        :class="[
                          $style.trendValue,
                          metric.trend > 0 && $style.positive,
                          metric.trend < 0 && $style.negative,
                          metric.trend === 0 && $style.neutral,
                        ]"
                      >
                        <IconTrendingUp
                          v-if="metric.trend > 0"
                          width="24"
                          height="24"
                        />
                        <IconTrendingDown
                          v-else-if="metric.trend < 0"
                          width="24"
                          height="24"
                        />
                        <IconMinus v-else width="24" height="24" />
                        {{ formatPercentage(metric.trend) }}
                      </div>
                    </div>
                  </div>
                </div>
                <BaseButton size="large" :class="$style.categoryButton">
                  {{ localizedStrings['deep-dive'] }}
                </BaseButton>
              </div>
              <div
                v-if="selectedCategory === category.id"
                :class="[$style.view, $style.mobile]"
                @click.stop
              >
                <div :class="$style.viewHeader">
                  <BaseHeading size="h6" as="h2">
                    {{ computedViews[selectedCategory].title }}
                  </BaseHeading>
                  <button :class="$style.close" @click="handleClose">
                    <IconClose width="40" height="40" />
                  </button>
                </div>
                <MetricsDashboardFilters
                  :stores="stores"
                  :selected-stores="selectedStores"
                  :should-open-select="shouldOpenSelect"
                  :metrics="computedViews[selectedCategory].metrics"
                  :selected-category="selectedCategory"
                  :selected-metric-filter="selectedMetricFilter"
                  :selected-date-filter="selectedDateFilter"
                  :selected-view="selectedView"
                  :select-metric-filter="selectMetricFilter"
                  :select-date-filter="selectDateFilter"
                  :select-view="selectView"
                  :handle-select="handleSelect"
                  :toggle-select="toggleSelect"
                />
                <div :class="$style.insight">
                  <IconLightbulb
                    width="12"
                    height="16"
                    :class="$style.lightbulb"
                  />
                  <MarkedParser
                    :md="computedViews[selectedCategory].insight"
                    :class="['u-fs-p1', $style.insightCopy]"
                    @click.stop="handleInsightClick"
                  />
                </div>
                <div :class="$style.viewContent">
                  <Line
                    v-if="selectedView === 'cumulative'"
                    :data="
                      computedViews[selectedCategory].data[
                        selectedMetricFilter
                      ] as ChartData<'line', number[], unknown>
                    "
                    :options="computedChartOptions"
                  />
                  <Bar
                    v-else
                    :data="
                      computedViews[selectedCategory].data[
                        selectedMetricFilter
                      ] as ChartData<'bar', number[], unknown>
                    "
                    :options="computedChartOptions"
                  />
                </div>
                <MarkedParser
                  :md="
                    computedViews[selectedCategory].description[
                      selectedMetricFilter
                    ]
                  "
                  :class="['u-fs-p1', $style.description]"
                />
                <MetricsDashboardReviews
                  :selected-category="selectedCategory"
                  :reviews="computedReviews"
                  :is-v2="isV2"
                  :quarter-review-count="computedQuarterReviewCount"
                  :quarter-review-count-copy="
                    data?.fields?.quarterReviewCountCopy || ''
                  "
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="missingData" :class="$style.missingDataError">
          <div :class="$style.error">
            {{ computedErrorText }}
            <CallToAction
              v-if="data.fields.errorCta"
              :cta="data.fields.errorCta"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="selectedCategory" :class="[$style.view, $style.desktop]">
      <div :class="$style.viewHeader">
        <BaseHeading size="h6" as="h2">
          {{ computedViews[selectedCategory].title }}
        </BaseHeading>
        <button :class="$style.close" @click="handleClose">
          <IconClose width="40" height="40" />
        </button>
      </div>
      <MetricsDashboardFilters
        :stores="stores"
        :selected-stores="selectedStores"
        :should-open-select="shouldOpenSelect"
        :metrics="computedViews[selectedCategory]?.metrics"
        :selected-category="selectedCategory"
        :selected-metric-filter="selectedMetricFilter"
        :selected-date-filter="selectedDateFilter"
        :selected-view="selectedView"
        :select-metric-filter="selectMetricFilter"
        :select-date-filter="selectDateFilter"
        :select-view="selectView"
        :handle-select="handleSelect"
        :toggle-select="toggleSelect"
      />
      <div :class="$style.viewContent">
        <Line
          v-if="selectedView === 'cumulative'"
          :data="
            computedViews[selectedCategory].data[
              selectedMetricFilter
            ] as ChartData<'line', number[], unknown>
          "
          :options="computedChartOptions"
        />
        <Bar
          v-else
          :data="
            computedViews[selectedCategory].data[
              selectedMetricFilter
            ] as ChartData<'bar', number[], unknown>
          "
          :options="computedChartOptions"
        />
      </div>
      <MarkedParser
        :md="computedViews[selectedCategory].description[selectedMetricFilter]"
        :class="['u-fs-p1', $style.description]"
      />
      <MetricsDashboardReviews
        :selected-category="selectedCategory"
        :reviews="computedReviews"
        :is-v2="isV2"
        :quarter-review-count="computedQuarterReviewCount"
        :quarter-review-count-copy="data?.fields?.quarterReviewCountCopy || ''"
      />
      <div :class="$style.insight">
        <IconLightbulb width="12" height="16" :class="$style.lightbulb" />
        <MarkedParser
          :md="computedViews[selectedCategory].insight"
          :class="['u-fs-p1', $style.insightCopy]"
          @click.stop="handleInsightClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TypeEventSkeleton,
  TypeEventWithoutUnresolvableLinksResponse,
  TypeMetricsDashboardWithoutUnresolvableLinksResponse,
  TypeProfileMetricsWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { StyleValue } from 'vue'
import { Line, Bar } from 'vue-chartjs'
import pushToGaDataLayer from '~/utils/pushToGaDataLayer'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  type ChartData,
} from 'chart.js'
import AnnotationPlugin, {
  type AnnotationOptions,
  type AnnotationTypeRegistry,
  type BoxLabelOptions,
  type EventContext,
  type PartialEventContext,
} from 'chartjs-plugin-annotation'

import { formatLocalizedDate } from '~/utils/localizeDates'
import {
  compareAsc,
  // format,
  subDays,
  differenceInDays,
  eachDayOfInterval,
  getDay,
  differenceInCalendarDays,
  startOfWeek,
  startOfQuarter,
  endOfQuarter,
} from 'date-fns'
import type { IProfileMetrics, IProfileMetricsV2 } from '~/types/wpnStore'

const LINE_COLORS = [
  '#36a2eb',
  '#ff6384',
  '#4bc0c0',
  '#ff9f40',
  '#9966ff',
  '#ffcd56',
  '#c9cbcf',
]

const LegendPlugin = {
  id: 'increase-legend-spacing',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  beforeInit(chart: any) {
    // Get reference to the original fit function
    const originalFit = chart.legend.fit

    // Override the fit function
    chart.legend.fit = function fit() {
      // Call original function and bind scope in order to use `this` correctly inside it
      originalFit.bind(chart.legend)()
      this.height += 32
    }
  },
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  LegendPlugin,
  AnnotationPlugin,
)

interface IProfileChartEvent {
  id: string
  days: IDaysOfTheWeek[] | undefined
  startDatetime: string | Date | undefined
  endDatetime: string | Date | undefined
  gridColumnStart: number
  gridColumnEnd: number
  setAbbreviation: string | undefined
  eventType: string | undefined
  event: TypeEventWithoutUnresolvableLinksResponse
  sequence: {
    id: string
    startDatetime: string | Date | undefined
    endDatetime: string | Date | undefined
    gridColumnStart: number
    gridColumnEnd: number
  }[]
}

const route = useRoute()
const { locale } = useI18n()
const { $client, $visibility, $userAccessCheck } = useNuxtApp()

const props = defineProps<{
  data: TypeMetricsDashboardWithoutUnresolvableLinksResponse
}>()

const userStore = useUserStore()
const { stores } = storeToRefs(userStore)
const { highestUserLevel } = storeToRefs(userStore)

const currentStore = ref(
  stores.value?.find((store) => store?.id === arrayAsString(route.query.s)) ||
    stores.value?.[0],
)
const selectedStores = ref(
  currentStore.value ? new Set([currentStore.value.id]) : new Set(),
)
const shouldOpenSelect = ref(false)

const { config, showLegacyMetrics } = useLegacyMetricsView()

// Whether any store's country is eligible for the new metrics
const hasNonLegacyMetricsStore = () =>
  stores.value?.some(
    (store) =>
      store &&
      !checkIsLegacyMetricsCountry(store, config.value.legacyMetricsCountries),
  ) ?? false

// Skip the live metrics fetch when no store can view the new metrics.
const testProfileMetricsData = await useTestProfileMetrics({
  skipLiveFetch: () => !hasNonLegacyMetricsStore(),
})

const missingData = ref(
  !(
    currentStore.value &&
    Boolean(
      testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier,
    )
  ),
)

const currentTier = ref(
  (currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
      ?.current?.value) ||
    0,
)
const rollingTier = ref(
  (currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
      ?.rolling) ||
    0,
)
// const maxTier = ref(6)
// hide graph on page load.
const selectedCategory = ref('')
const selectedMetricFilter = ref('customer-review-rating')
const selectedDateFilter = ref<30 | 90>(30)
const selectedView = ref<'weekly' | 'cumulative'>('weekly')

const deviceWidth = ref(1440)

const { localizedStrings } = useLocalizedStrings()

const { data: eventsResponse } = await useAsyncData(
  'metrics-dashboard-events',
  async () => {
    // Skip fetch when no store can view the new metrics
    if (!hasNonLegacyMetricsStore()) {
      return { events: [] }
    }

    const today = new Date()
    const startDay = subDays(today, selectedDateFilter.value)
    const endDay = today

    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        include: 10,
        limit: 500,
        locale: locale.value,
        'fields.startDatetime[lte]': formatAsContentfulDate(endDay),
        'fields.endDatetime[gte]': formatAsContentfulDate(startDay),
        'fields.showInProfileMetricsCharts': true,
        order: ['fields.startDatetime'],
        select: [
          'sys.id',
          'fields.entryTitle',
          'fields.days',
          'fields.icons',
          'fields.title',
          'fields.path',
          'fields.pathOverride',
          'fields.color',
          'fields.backgroundColor',
          'fields.startDatetime',
          'fields.endDatetime',
          'fields.visibility',
          'fields.accessLevel',
          'fields.associatedBrand',
          'fields.associatedProduct',
          'fields.eventType',
        ],
      })

    const filtered = response.items.filter((event) => {
      const hasAccess = $userAccessCheck(
        event.fields?.accessLevel,
        highestUserLevel.value,
      )
      const isVisible = $visibility(event.fields?.visibility)
      return hasAccess && isVisible
    })

    return { events: filtered }
  },
  {
    transform(input) {
      return {
        events: input.events,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [selectedDateFilter],
  },
)

const isGamestopStore = computed(() => checkIsGamestopStore(currentStore.value))

const isV2 = computed(() => {
  const metricsData =
    currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]
  const metricsKeys = metricsData && Object.keys(metricsData?.metrics)
  return metricsKeys?.includes('tickets_weekly') || false
})

const computedErrorText = computed(() => {
  const id = props.data.fields.errorText?.fields?.id || ''
  return localizedStrings.value[id] || ''
})

const computedEvents = computed(() => {
  const today = new Date()
  const startDay = subDays(today, selectedDateFilter.value)
  const endDay = today

  const simplifiedEvents = eventsResponse.value?.events?.map?.((event) => {
    const startDatetime = event?.fields?.startDatetime || startDay
    const endDatetime = event?.fields?.endDatetime || endDay

    return {
      event,
      id: event?.sys?.id,
      days: event?.fields?.days,
      startDatetime,
      endDatetime,
      gridColumnStart: differenceInDays(startDatetime, startDay) + 1,
      gridColumnEnd: differenceInDays(endDatetime, startDay) + 1,
      setAbbreviation:
        event?.fields?.associatedProduct?.fields?.setAbbreviation,
      eventType: event?.fields?.eventType?.fields?.label,
      sequence: [],
    }
  })

  const eventsByRow = (simplifiedEvents || []).reduce(
    (eventsSoFar: IProfileChartEvent[], event: IProfileChartEvent) => {
      if (event?.days) {
        const eventDays = eachDayOfInterval({
          start: new Date(event?.startDatetime || ''),
          end: new Date(event?.endDatetime || ''),
        }).filter((eventDay) => {
          return event?.days?.includes(DAYS_OF_THE_WEEK[getDay(eventDay)])
        })

        const eventsBySequence = eventDays.reduce(
          (
            eventsBySequenceSoFar: IProfileChartEvent['sequence'],
            eventDay: Date,
            eventDayIndex: number,
          ) => {
            const [previousEvent] = eventsBySequenceSoFar.slice(-1)

            if (
              previousEvent &&
              differenceInCalendarDays(
                eventDay,
                new Date(previousEvent?.endDatetime || ''),
              ) === 1
            ) {
              return [
                ...eventsBySequenceSoFar.slice(
                  0,
                  eventsBySequenceSoFar.length - 1,
                ),
                {
                  ...previousEvent,
                  endDatetime: eventDay,
                  gridColumnEnd: differenceInCalendarDays(eventDay, startDay),
                },
              ]
            }

            return [
              ...eventsBySequenceSoFar,
              {
                id: `${event?.id}-${eventDayIndex}`,
                startDatetime: eventDay,
                endDatetime: eventDay,
                gridColumnStart: differenceInCalendarDays(eventDay, startDay),
                gridColumnEnd: differenceInCalendarDays(eventDay, startDay) + 1,
              },
            ]
          },
          [],
        )

        return [...eventsSoFar, { ...event, sequence: eventsBySequence }]
      }

      return [...eventsSoFar, event]
    },
    [],
  )

  return eventsByRow
})

const computedCopy = computed(() => {
  const copy = props.data?.fields?.copy

  if (copy?.includes(`{{ projectedTier }}`)) {
    const projectedTier = String(Math.floor(rollingTier.value))

    return copy.replace(
      `{{ projectedTier }}`,
      localizedStrings.value[`tier${projectedTier}`],
    )
  }

  return copy
})

const computedTrends = computed(() => {
  const metrics =
    currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]?.metrics

  return {
    'customer-review-rating': metrics?.csat?.trend.percentage || 0,
    'new-players': metrics?.new_to_op_players?.trend.percentage || 0,
    tickets: metrics?.tickets?.trend.percentage || 0,
    'event-diversity': metrics?.event_diversity?.trend.percentage || 0,
    'on-time-event-schedule':
      metrics?.calendar_up_to_date?.trend.percentage || 0,
  }
})

const computedCategories = computed(() => {
  const metricsData =
    currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]

  const metrics = metricsData?.metrics

  const customerExperienceStatus =
    metricsData?.customer_experience_performance_category || ''
  const playStatus =
    (metricsData as IProfileMetrics)?.play_performance_category || ''
  const communityStatus =
    (metricsData as IProfileMetrics)?.community_performance_category || ''

  const newPlayersStatus =
    (metricsData as IProfileMetricsV2)?.new_players_performance_category || ''
  const ticketsStatus =
    (metricsData as IProfileMetricsV2)?.tickets_performance_category || ''
  const eventDiversityStatus =
    (metricsData as IProfileMetricsV2)?.event_variety_performance_category || ''
  const onTimeEventScheduleStatus =
    (metricsData as IProfileMetricsV2)
      ?.on_time_event_schedule_performance_category || ''

  return [
    {
      id: 'customer-experience',
      name: localizedStrings.value['customer-experience'],
      status: !isV2.value
        ? customerExperienceStatus.toLowerCase().replace(/ /g, '-')
        : '',
      metrics: [
        {
          id: 'customer-review-rating',
          label: localizedStrings.value['customer-review-rating'],
          url: props.data?.fields?.customerReviewRatingUrl,
          tooltip: localizedStrings.value['customer-review-rating-tooltip'],
          status: isV2.value
            ? customerExperienceStatus.toLowerCase().replace(/ /g, '-')
            : '',
          type: 'percent',
          value: formatValue(metrics?.csat?.current.value || 0, 2),
          trend: computedTrends.value['customer-review-rating'],
        },
      ],
    },
    {
      id: 'play',
      name: localizedStrings.value['play'],
      status: !isV2.value ? playStatus.toLowerCase().replace(/ /g, '-') : '',
      metrics: [
        {
          id: 'new-players',
          label: localizedStrings.value['new-players'],
          url: props.data?.fields?.newPlayersUrl,
          tooltip: localizedStrings.value['new-players-tooltip'],
          status: isV2.value
            ? newPlayersStatus.toLowerCase().replace(/ /g, '-')
            : '',
          type: 'decimal',
          value: formatValue(metrics?.new_to_op_players?.current.value || 0),
          trend: computedTrends.value['new-players'],
        },
        {
          id: 'tickets',
          label: localizedStrings.value['tickets'],
          url: props.data?.fields?.ticketsUrl,
          tooltip: localizedStrings.value['tickets-tooltip'],
          status: isV2.value
            ? ticketsStatus.toLowerCase().replace(/ /g, '-')
            : '',
          type: 'decimal',
          value: formatValue(metrics?.tickets?.current.value || 0),
          trend: computedTrends.value['tickets'],
        },
      ],
    },
    {
      id: 'community',
      name: localizedStrings.value['community'],
      status: !isV2.value
        ? communityStatus.toLowerCase().replace(/ /g, '-')
        : '',
      metrics: [
        {
          id: 'event-diversity',
          label: localizedStrings.value['event-diversity'],
          url: props.data?.fields?.eventVarietyUrl,
          tooltip: localizedStrings.value['event-diversity-tooltip'],
          status: isV2.value
            ? eventDiversityStatus.toLowerCase().replace(/ /g, '-')
            : '',
          type: 'decimal',
          value: formatValue(metrics?.event_diversity?.current.value || 0),
          trend: computedTrends.value['event-diversity'],
        },
        {
          id: 'on-time-event-schedule',
          label: localizedStrings.value['on-time-event-schedule'],
          url: props.data?.fields?.onTimeSchedulingUrl,
          tooltip: localizedStrings.value['on-time-event-schedule-tooltip'],
          status: isV2.value
            ? onTimeEventScheduleStatus.toLowerCase().replace(/ /g, '-')
            : '',
          type: 'percent',
          value: formatValue(
            metrics?.calendar_up_to_date?.current.value || 0,
            2,
          ),
          trend: computedTrends.value['on-time-event-schedule'],
        },
      ],
    },
  ]
})

const computedViews = computed<{
  [key: string]: {
    title: string
    metrics: string[]
    data: {
      [key: string]: ChartData<'line' | 'bar', number[], unknown>
    }
    description: {
      [key: string]: string
    }
    insight: string
  }
}>(() => {
  return {
    'customer-experience': {
      title: localizedStrings.value['customer-experience-trend'],
      metrics: ['customer-review-rating'],
      data: {
        'customer-review-rating': formatDatasets(
          'csat',
          selectedDateFilter.value,
          selectedView.value,
        ),
      },
      description: {
        'customer-review-rating':
          localizedStrings.value[
            'customer-review-rating-' + selectedView.value + '-description'
          ],
      },
      insight:
        localizedStrings.value['improve-your-customer-experience-trend-by'],
    },
    play: {
      title: localizedStrings.value['play-trend'],
      metrics: ['new-players', 'tickets'],
      data: {
        'new-players': formatDatasets(
          'new_to_op_players',
          selectedDateFilter.value,
          selectedView.value,
        ),
        tickets: formatDatasets(
          'tickets',
          selectedDateFilter.value,
          selectedView.value,
        ),
      },
      description: {
        'new-players':
          localizedStrings.value[
            'new-players-' + selectedView.value + '-description'
          ],
        tickets:
          localizedStrings.value[
            'tickets-' + selectedView.value + '-description'
          ],
      },
      insight: localizedStrings.value['improve-your-play-trend-by'],
    },
    community: {
      title: localizedStrings.value['community-trend'],
      metrics: ['event-diversity', 'on-time-event-schedule'],
      data: {
        'event-diversity': formatDatasets(
          'event_diversity',
          selectedDateFilter.value,
          selectedView.value,
        ),
        'on-time-event-schedule': formatDatasets(
          'calendar_up_to_date',
          selectedDateFilter.value,
          selectedView.value,
        ),
      },
      description: {
        'event-diversity':
          localizedStrings.value[
            'event-diversity-' + selectedView.value + '-description'
          ],
        'on-time-event-schedule':
          localizedStrings.value[
            'on-time-event-schedule-' + selectedView.value + '-description'
          ],
      },
      insight: localizedStrings.value['improve-your-community-trend-by'],
    },
  }
})

const computedPointRadius = computed(() => {
  let deviceWidthThreshold = 768

  // Hide UI for data points sooner for larger dataset view
  if (selectedDateFilter.value === 90) {
    deviceWidthThreshold = 1024
  }

  return deviceWidth.value >= deviceWidthThreshold ? undefined : 0
})

const computedAnnotations = computed(() => {
  if (selectedView.value === 'weekly') return []

  const eventsByRow = computedEvents.value

  const annotations: Record<
    string,
    AnnotationOptions<keyof AnnotationTypeRegistry>
  > = {}

  for (const e of eventsByRow) {
    if (!e) continue

    let backgroundColor =
      e.event.fields?.backgroundColor || 'rgba(0, 0, 0, 0.33)'
    if (backgroundColor.startsWith('rgb(')) {
      backgroundColor = backgroundColor
        .replace('rgb', 'rgba')
        .replace(')', ', 0.30)')
    } else if (backgroundColor.startsWith('hsl(')) {
      backgroundColor = backgroundColor
        .replace('hsl', 'hsla')
        .replace(')', ', 0.30)')
    } else if (backgroundColor.startsWith('#')) {
      const hex = backgroundColor.replace('#', '')
      if (hex.length === 3) {
        const h1 = hex.at(0)
        const h2 = hex.at(1)
        const h3 = hex.at(2)
        backgroundColor = `#${h1}${h1}${h2}${h2}${h3}${h3}`
      }
      backgroundColor += '1E' // 30% opacity
    }

    let label: BoxLabelOptions | undefined = undefined
    const enter = (ctx: EventContext & { hovered: boolean }) => {
      ctx.hovered = true
      ctx.chart.update()
    }
    const leave = (ctx: EventContext & { hovered: boolean }) => {
      ctx.hovered = false
      ctx.chart.update()
    }
    if (e?.setAbbreviation || e?.eventType) {
      const content = []

      // Get event name to display in box annotation label
      let eventNameLabel = ''
      if (e?.setAbbreviation) {
        eventNameLabel = e?.setAbbreviation
        if (e?.eventType) eventNameLabel += ' '
      }
      if (e?.eventType) {
        eventNameLabel += e?.eventType
      }
      if (eventNameLabel) {
        content.push(eventNameLabel)
      }

      // Get event datetime to display in box annotation label
      let eventDatetimeLabel = ''
      if (e?.event?.fields?.startDatetime) {
        eventDatetimeLabel = formatLocalizedDate(
          e?.event?.fields?.startDatetime,
          'MM/dd/yyyy',
          locale.value,
        )
        if (e?.event?.fields?.endDatetime) {
          eventDatetimeLabel += '–'
        }
      }
      if (e?.event?.fields?.endDatetime) {
        eventDatetimeLabel += formatLocalizedDate(
          e?.event?.fields?.endDatetime,
          'MM/dd/yyyy',
          locale.value,
        )
      }
      if (eventDatetimeLabel) {
        content.push(eventDatetimeLabel)
      }

      label = {
        display: (ctx: PartialEventContext) =>
          !!(ctx as PartialEventContext & { hovered?: boolean }).hovered,
        color: e.event.fields?.backgroundColor || '#000',
        textStrokeColor: '#FFF',
        textStrokeWidth: 2,
        drawTime: 'afterDatasetsDraw',
        textAlign: 'center',
        content,
      }
    }

    if (e.sequence?.length) {
      for (const s of e.sequence) {
        if (!s) continue
        annotations[s.id] = {
          type: 'box' as const,
          xMin: s.gridColumnStart - 1,
          xMax:
            s.gridColumnEnd >= selectedDateFilter.value
              ? selectedDateFilter.value - 2
              : s.gridColumnEnd - 1,
          backgroundColor,
          ...(label ? { label } : {}),
          enter,
          leave,
          click: () => {
            if (e?.event?.fields?.pathOverride || e?.event?.fields?.path) {
              navigateTo(
                {
                  path: `/event/${e?.event?.fields?.pathOverride || e?.event?.fields?.path}`,
                },
                {
                  open: {
                    target: '_blank',
                  },
                },
              )
            }
          },
        }
      }
    } else {
      annotations[e.id] = {
        type: 'box' as const,
        xMin: e.gridColumnStart - 1,
        xMax:
          e.gridColumnEnd >= selectedDateFilter.value
            ? selectedDateFilter.value - 2
            : e.gridColumnEnd - 1,
        backgroundColor,
        ...(label ? { label } : {}),
        enter,
        leave,
        click: () => {
          if (e?.event?.fields?.pathOverride || e?.event?.fields?.path) {
            navigateTo(
              {
                path: `/event/${e?.event?.fields?.pathOverride || e?.event?.fields?.path}`,
              },
              {
                open: {
                  target: '_blank',
                },
              },
            )
          }
        },
      }
    }
  }

  return annotations
})

const computedFormatChartAsPercentage = computed(() => {
  return (
    !(
      selectedMetricFilter.value === 'customer-review-rating' &&
      isV2.value &&
      selectedView.value === 'weekly'
    ) &&
    ['customer-review-rating', 'on-time-event-schedule'].includes(
      selectedMetricFilter.value,
    )
  )
})

const computedChartOptions = computed(() => {
  return {
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: localizedStrings.value['date'],
          color: '#000',
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          callback: function (val: any) {
            if (selectedView.value === 'weekly') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              return (this as any).getLabelForValue(val)
            }

            return formatLocalizedDate(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (this as any).getLabelForValue(val),
              'MMM d',
              locale.value,
            ).toUpperCase()
          },
        },
      },
      y: {
        min: computedFormatChartAsPercentage.value ? 0 : undefined,
        max: computedFormatChartAsPercentage.value ? 1 : undefined,
        title: {
          display: true,
          text:
            selectedMetricFilter.value === 'customer-review-rating' &&
            isV2.value &&
            selectedView.value === 'weekly'
              ? localizedStrings.value['count-of-reviews']
              : localizedStrings.value[selectedMetricFilter.value],
          color: '#000',
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          callback: function (val: any) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const labelForValue: string = (this as any).getLabelForValue(val)

            if (computedFormatChartAsPercentage.value) {
              return formatPercentage(parseFloat(labelForValue))
            } else if (Math.floor(val) === val) {
              return labelForValue
            }
          },
        },
      },
    },
    elements: {
      point: {
        radius: computedPointRadius.value,
      },
    },
    layout: {
      padding: {
        right: deviceWidth.value >= 1024 ? 48 : 64,
      },
    },
    plugins: {
      legend: {
        align: 'start' as const,
        labels: {
          font: { size: 14, family: "'Open Sans', Arial, sans-serif" },
          usePointStyle: true,
          color: '#000',
          padding: 16,
        },
      },
      tooltip: {
        yAlign: 'bottom' as const,
        callbacks: {
          title: () => {
            return ''
            // let date = ctx[0].label

            // // Update labels if part of previous trend
            // if (
            //   ctx[0].dataset.label.includes(
            //     localizedStrings.value['previous-trend'] || 'Previous Trend',
            //   )
            // ) {
            //   date = format(
            //     subDays(new Date(date), selectedDateFilter.value),
            //     'MM/dd/yyyy',
            //   )
            // }

            // return formatLocalizedDate(
            //   date,
            //   'MMM d',
            //   locale.value,
            // ).toUpperCase()
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          labelColor: (ctx: any) => {
            return {
              borderColor: 'rgb(0, 0, 0, 0)',
              backgroundColor: ctx.dataset.borderColor,
              borderRadius: 5,
            }
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (ctx: any) => {
            return ctx.dataset.label
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          footer: (ctx: any) => {
            const indent = '    '

            const regex = /^(.*?)\s+\(/
            const name = ctx[0].dataset.label.includes('(')
              ? ctx[0].dataset.label.match(regex)[1]
              : ctx[0].dataset.label

            let formattedValue = ctx[0].formattedValue

            if (computedFormatChartAsPercentage.value) {
              formattedValue = formatPercentage(parseFloat(formattedValue))
            }

            return `${indent}${localizedStrings.value['store-id'] || 'Store ID:'} ${computedStoresMap.value[name]?.id}\n${indent}${formattedValue}`
          },
        },
        caretPadding: 8,
        caretSize: 0,
        padding: 10,
        backgroundColor: '#fff',
        titleColor: '#000',
        bodyColor: '#000',
        bodyFont: {
          weight: 'bold' as const,
        },
        footerColor: '#000',
        footerFont: {
          lineHeight: 1.4,
        },
        borderColor: '#000',
        borderWidth: 1,
      },
      annotation: {
        clip: false,
        annotations: computedAnnotations.value,
      },
    },
  }
})

const computedStoresMap = computed(() => {
  if (!stores.value) return {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return stores.value.reduce((acc: any, curr: any) => {
    if (!curr) return acc

    acc[curr.name] = {
      id: curr.id,
      address: curr.address,
    }
    return acc
  }, {})
})

const computedReviews = computed(() => {
  const reviewsData =
    currentStore.value &&
    (
      testProfileMetricsData.value?.metrics[
        currentStore.value.id
      ] as IProfileMetrics
    )?.reviews

  const positiveReviewsData =
    currentStore.value &&
    (
      testProfileMetricsData.value?.metrics[
        currentStore.value.id
      ] as IProfileMetricsV2
    )?.reviews_positive

  const negativeReviewsData =
    currentStore.value &&
    (
      testProfileMetricsData.value?.metrics[
        currentStore.value.id
      ] as IProfileMetricsV2
    )?.reviews_negative

  // Determine order to display types of reviews
  const order = [
    'product',
    'staff',
    'management',
    'attendance',
    'environment',
    'community',
    'other',
  ] as (keyof IProfileMetrics['reviews'])[]

  return order
    .map((key) => {
      // Hide any rows that have null data
      const count = reviewsData?.[key]
      const positive = positiveReviewsData?.[key]
      const negative = negativeReviewsData?.[key]
      if (count == null && positive == null && negative == null) {
        return null
      }

      return {
        key,
        count,
        positive,
        negative,
        reason: localizedStrings.value[`${key}-reviews-reason`] || '',
      }
    })
    .filter(Boolean) as {
    key: string
    count: number | undefined | null
    positive: number | undefined | null
    negative: number | undefined | null
    reason: string
  }[]
})

const computedQuarterReviewCount = computed(() => {
  if (!isV2.value || !currentStore.value) return 0

  const metricsData = testProfileMetricsData.value?.metrics as unknown as {
    [key: string]: IProfileMetricsV2
  }
  const reviewsCountData =
    metricsData?.[currentStore.value.id]?.metrics?.reviews_count_weekly

  const now = new Date()
  const quarterStart = startOfQuarter(now)
  const quarterEnd = endOfQuarter(now)

  const weekMap = new Map<string, { date: string; value: number }>()
  reviewsCountData?.history?.forEach((curr) => {
    if (curr?.value == null) return
    const date = new Date(formatRawDate(curr.date))
    if (date < quarterStart || date > quarterEnd) return
    const weekKey = startOfWeek(date, { weekStartsOn: 1 }).toISOString()
    const existing = weekMap.get(weekKey)
    if (!existing || curr.date > existing.date) {
      weekMap.set(weekKey, { date: curr.date, value: curr.value })
    }
  })
  const quarterReviewCount = Array.from(weekMap.values()).reduce(
    (acc, { value }) => acc + value,
    0,
  )

  return quarterReviewCount || 0
})

const selectCategory = (category: string) => {
  const previousCategory = selectedCategory.value

  selectedCategory.value = category
  selectedMetricFilter.value = computedViews.value[category].metrics[0]
  selectedDateFilter.value = 30
  selectedView.value =
    selectedCategory.value !== 'community' ? 'weekly' : 'cumulative'

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'metric_data_selected',
    eventParams: {
      ...defaultEventParams.value,
      previous_metric_category: previousCategory,
      metric_category: selectedCategory.value,
    },
  })
}

const selectMetricFilter = (id: string) => {
  const previousMetricFilter = selectedMetricFilter.value

  selectedMetricFilter.value = id

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'metric_chart_sub_metric_selected',
    eventParams: {
      ...defaultEventParams.value,
      metric_category: selectedCategory.value,
      metric_name: selectedMetricFilter.value,
      previous_metric_name: previousMetricFilter,
      active_time_filter: `${selectedDateFilter.value} and ${selectedView.value}`,
    },
  })
}

const selectDateFilter = (maxDaysAgo: 30 | 90) => {
  const previousDateFilter = selectedDateFilter.value

  selectedDateFilter.value = maxDaysAgo

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'metric_chart_time_filter_changed',
    eventParams: {
      ...defaultEventParams.value,
      metric_category: selectedCategory.value,
      metric_name: selectedMetricFilter.value,
      time_filter: `${selectedDateFilter.value} and ${selectedView.value}`,
      previous_time_filter: `${previousDateFilter} and ${selectedView.value}`,
    },
  })
}

const selectView = (view: 'weekly' | 'cumulative') => {
  const previousView = selectedView.value

  selectedView.value = view

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'metric_chart_time_filter_changed',
    eventParams: {
      ...defaultEventParams.value,
      metric_category: selectedCategory.value,
      metric_name: selectedMetricFilter.value,
      time_filter: `${selectedDateFilter.value} and ${selectedView.value}`,
      previous_time_filter: `${selectedDateFilter.value}  and ${previousView}`,
    },
  })
}

const handleClose = () => {
  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'metric_chart_closed',
    eventParams: {
      ...defaultEventParams.value,
      metric_category: selectedCategory.value,
      metric_name: selectedMetricFilter.value,
      active_time_filter: `${selectedDateFilter.value} and ${selectedView.value}`,
      compare_stores_active: selectedStores.value.size > 1,
      stores_in_comparison: storesInComparison(),
    },
  })

  selectedCategory.value = ''
}

const handleInsightClick = (event: MouseEvent) => {
  const target = event.target
  const linkEl = target instanceof Element ? target.closest('a') : null

  if (!linkEl) return

  const link = linkEl.getAttribute('href') || ''

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'metric_info_click',
    eventParams: {
      ...defaultEventParams.value,
      metric_category: selectedCategory.value,
      metric_name: selectedMetricFilter.value,
      click_location: 'chart_footer',
      outbound: link.includes('wpn.wizards.com') ? false : true,
      link_url: link,
    },
  })
}

const formatPercentage = (value: number) => {
  if (!value) return null

  return Intl.NumberFormat(locale.value, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(Math.abs(value))
}

const formatValue = (value: number, maximumFractionDigits = 0) => {
  const factor = 10 ** maximumFractionDigits
  return Math.round((value || 0) * factor) / factor
}

const formatDatasets = (
  key: keyof IProfileMetricsV2['metrics'],
  maxDaysAgo: number = 30,
  type: 'weekly' | 'cumulative',
) => {
  const today = new Date()
  const currentTrendStartDate = subDays(today, maxDaysAgo)
  const previousTrendStartDate = subDays(today, maxDaysAgo * 2)

  let actualKey =
    `${key}${isV2.value && type === 'weekly' ? '_weekly' : ''}` as keyof IProfileMetricsV2['metrics']
  if (key === 'csat' && isV2.value && type === 'weekly') {
    actualKey = 'reviews_count_weekly'
  }

  const metrics =
    (testProfileMetricsData.value?.metrics as unknown as {
      [key: string]: IProfileMetricsV2
    }) || {}
  const currentHistory =
    (currentStore.value &&
      metrics[currentStore.value.id]?.metrics[actualKey]?.history) ||
    []

  let idx = 0
  const datasets = stores.value?.reduce<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { id: string; dataset: { [key: string]: any } }[]
  >((acc, curr) => {
    const history =
      (curr && metrics[curr.id]?.metrics[actualKey]?.history) || []

    const baseConfig = {
      label:
        type === 'cumulative'
          ? `${curr?.name} (${localizedStrings.value['current-trend'] || 'Current Trend'})`
          : curr?.name,
      tension: 0.5,
      borderColor: LINE_COLORS[idx % LINE_COLORS.length],
      backgroundColor: LINE_COLORS[idx % LINE_COLORS.length] + '80', // Set to about 50% opacity
    }

    let data

    const filteredHistory = history
      .filter(
        ({ date }) =>
          compareAsc(formatRawDate(date), currentTrendStartDate) >= 0,
      )
      .reverse()
    if (type === 'weekly') {
      const weeklyHistory = aggregateWeeklyData(
        filteredHistory,
        locale.value,
        isV2.value,
      )
      data = Object.values(weeklyHistory)
    } else {
      data = filteredHistory
    }

    const currentTrend = {
      id: curr?.id || '',
      dataset: {
        ...baseConfig,
        data: data.map(({ value }) => value),
      },
    }

    if (type === 'cumulative') {
      const previousTrend = {
        id: curr?.id || '',
        dataset: {
          ...baseConfig,
          label: `${curr?.name} (${localizedStrings.value['previous-trend'] || 'Previous Trend'})`,
          borderDash: [5, 5],
          borderColor: LINE_COLORS[idx % LINE_COLORS.length] + '80', // Set to about 50% opacity
          backgroundColor: LINE_COLORS[idx % LINE_COLORS.length] + '19', // Set to about 25% opacity
          data: history
            .filter(
              ({ date }) =>
                compareAsc(formatRawDate(date), previousTrendStartDate) >= 0 &&
                compareAsc(formatRawDate(date), currentTrendStartDate) < 0,
            )
            .reverse()
            .map(({ value }) => value),
        },
      }
      idx++
      return [...acc, currentTrend, previousTrend]
    } else {
      idx++
      return [...acc, currentTrend]
    }
  }, [])

  const filtered = datasets.filter(({ id }) => selectedStores.value.has(id))

  const filteredHistory = currentHistory
    .filter(Boolean) // Just in case first item of S3 data is null
    .filter(
      ({ date }) => compareAsc(formatRawDate(date), currentTrendStartDate) >= 0,
    )
    .reverse()

  if (type === 'weekly') {
    const weeklyHistory = aggregateWeeklyData(
      filteredHistory,
      locale.value,
      isV2.value,
    )
    return {
      labels: Object.values(weeklyHistory).map(({ label }) => label),
      datasets: filtered.map(({ dataset }) => {
        return dataset
      }),
    } as ChartData<'line' | 'bar', number[], unknown>
  }

  return {
    labels: filteredHistory.map(({ date }) => formatRawDate(date)),
    datasets: filtered.map(({ dataset }) => {
      return dataset
    }),
  } as ChartData<'line' | 'bar', number[], unknown>
}

const handleSelect = (checked: boolean, id: string) => {
  const newSelected = new Set(selectedStores.value)

  if (checked) {
    newSelected.add(id)
  } else {
    newSelected.delete(id)
  }

  selectedStores.value = newSelected

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'metric_chart_store_comparison_changed',
    eventParams: {
      ...defaultEventParams.value,
      metric_category: selectedCategory.value,
      metric_name: selectedMetricFilter.value,
      comparison_action: checked ? 'add' : 'remove',
      stores_in_comparison: storesInComparison(),
    },
  })
}

const storesInComparison = () =>
  Array.from(selectedStores.value).map((storeId) => {
    const store = stores.value?.find((each) => each?.id === storeId)
    return store ? `${store.name} (${storeId})` : storeId
  })

const toggleSelect = () => {
  shouldOpenSelect.value = !shouldOpenSelect.value

  // open select store dropdown
  if (shouldOpenSelect.value) {
    pushToGaDataLayer({
      event: 'ga4.trackEvent',
      eventName: 'metric_chart_compare_stores_opened',
      eventParams: {
        ...defaultEventParams.value,
        metric_category: selectedCategory.value,
        metric_name: selectedMetricFilter.value,
      },
    })
  }
}

const onWindowResize = () => {
  deviceWidth.value = window.innerWidth
}

onMounted(() => {
  onWindowResize()
  window.addEventListener('resize', onWindowResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize)
})

const defaultEventParams = computed(() => {
  return {
    retailer_current_tier: String(currentTier.value),
    store: currentStore.value?.name + ' (' + currentStore.value?.id + ')',
  }
})

watch(
  () => [route.query.s],
  async ([s]) => {
    currentStore.value =
      stores.value?.find((store) => store?.id === arrayAsString(s)) ||
      stores.value?.[0]
    selectedStores.value = currentStore.value
      ? new Set([currentStore.value.id])
      : new Set()

    currentTier.value =
      (currentStore.value &&
        testProfileMetricsData.value?.metrics?.[currentStore.value.id]
          ?.store_tier?.current?.value) ||
      0
    rollingTier.value =
      (currentStore.value &&
        testProfileMetricsData.value?.metrics?.[currentStore.value.id]
          ?.store_tier?.rolling) ||
      0
    missingData.value = !(
      currentStore.value &&
      Boolean(
        testProfileMetricsData.value?.metrics[currentStore.value.id]
          ?.store_tier,
      )
    )

    if (import.meta.client) {
      const dashboardVersion = showLegacyMetrics.value
        ? 'V1'
        : isGamestopStore.value
          ? 'GameStop'
          : 'V2'

      pushToGaDataLayer({
        event: 'ga4.trackEvent',
        eventName: 'dashboard_view',
        eventParams: {
          content: dashboardVersion,
          ...defaultEventParams.value,
        },
      })
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" module>
.config {
  --md-padding-top: 24px;
  --md-padding-bottom: 24px;
  --md-tier-progress-percentage: 50%;
}

.wrap {
  padding: var(--md-padding-top) 0 var(--md-padding-bottom);

  @include media('sm') {
    padding: var(--md-padding-top) 24px var(--md-padding-bottom);
  }
}

.inner {
  display: flex;
  flex-direction: column;
  background: var(--white);
  border-radius: var(--border-radius);
  box-shadow: 0 0 4px 0 rgba(#000, 0.25);
  padding: 0 24px 16px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
}

.headingWrap {
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-height: 40px;
}

.meta {
  display: flex;
  gap: 4px;
  padding: 10px 0;
  text-align: right;
  font-size: 14px;

  @include media('md') {
    text-align: left;
  }
}

.metaCopy {
  display: flex;
  flex-direction: column;

  a {
    font-weight: 700;
    text-decoration: underline;
    color: var(--purple);

    &:hover {
      color: var(--purple-light);
    }
  }

  @include media('md') {
    flex-direction: row;
    gap: 4px;
  }
}

.contentWrap {
  position: relative;
}

.copyWrap {
  margin-bottom: 32px;
}

.copy {
  font-size: to-rem(17px);
}

.tiers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 32px;

  @include media('xs') {
    justify-content: space-between;
  }

  @include media('sm') {
    gap: 8px;
  }
}

.tierWrap {
  display: flex;
  align-items: center;
  gap: 6px;

  &.grow {
    @include media('xs') {
      flex-grow: 1;
    }
  }

  @include media('sm') {
    gap: 8px;
  }
}

.tier {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 44px;
  height: 44px;
  border: 2px solid var(--purple);
  border-radius: 9999px;
  font-weight: 700;
  font-size: to-rem(16px);
  color: var(--purple);

  &.active {
    background: var(--purple);
    color: var(--white);
  }

  &.locked {
    border-color: var(--gray-4);
    background: var(--gray-4);
    color: var(--gray-8);
  }

  @include media('xs') {
    font-size: to-rem(18px);
  }

  @include media('sm') {
    width: 48px;
    height: 48px;
    font-size: to-rem(20px);
  }
}

.divider {
  display: flex;
  flex-grow: 1;
  height: 4px;
  background: var(--purple);
  border-radius: 9999px;
  min-width: 24px;

  &.active {
    background: linear-gradient(
      90deg,
      var(--purple) var(--md-tier-progress-percentage),
      var(--gray-4) var(--md-tier-progress-percentage)
    );
  }

  &.locked {
    background: var(--gray-4);
  }

  @include media('xxs') {
    min-width: 36px;
  }

  @media screen and (min-width: 400px) {
    min-width: 44px;
  }

  @include media('xs') {
    min-width: 24px;
  }
}

.categories {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @include media('md') {
    flex-direction: row;
  }
}

.categoryWrap {
  width: 100%;
}

.category {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 16px 24px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;

  &.active {
    border-color: var(--purple);
    background: var(--purple);
    color: var(--white);
  }

  &:hover {
    cursor: pointer;
    border-color: var(--purple);
  }
}

.categoryHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid #d3d3d3;
}

.categoryHeading {
  font-family: var(--base-font-family);
  font-size: to-rem(20px);
  font-weight: 700;
}

.tag {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 24px;
  font-weight: 700;

  &.excellent {
    background: #138034;
    color: var(--white);
  }

  &.needs-improvement {
    background: #f2c94c;
    color: var(--black);
  }

  &.good {
    background: #56ccf2;
    color: var(--black);
  }
}

.metrics {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @include media('xs') {
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 100%;
  }

  @include media('md') {
    display: flex;
    flex-direction: column;
    grid-template-columns: none;
  }

  @include media('xl') {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .singleColumn & {
    grid-template-columns: 1fr;
  }
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  @include media('md') {
    width: auto;
    flex-grow: 1;
  }
}

.metricLabelWrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.metricLabel {
  font-weight: 600;
  font-size: to-rem(16px);
  color: inherit;
}

.metricTooltip {
  font-size: to-rem(16px);
}

.tooltip {
  display: flex;
  width: min-content;
  height: min-content;
}

.questionMark {
  &:hover {
    cursor: pointer;
  }
}

.metricValueWrap {
  display: flex;
  align-items: center;
  gap: 8px;

  @include media('xs') {
    margin-top: auto;
  }
}

.metricValue {
  font-size: to-rem(36px);
  font-weight: 700;
}

.trendValue {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 60px;
  font-weight: 700;

  &.positive {
    background: #84e9b3;
    color: #086223;
  }

  &.negative {
    background: #ffa0a6;
    color: #821118;
  }

  &.neutral {
    background: #bdbdbd;
    color: #4f4f4f;
  }
}

.categoryButton {
  margin-top: auto;
  width: 100%;
  font-family: var(--base-font-family);
  font-size: to-rem(20px) !important;
  border-radius: 8px;

  .active & {
    background: #2d2b4d;

    &:hover {
      background: #24223e;
    }
  }
}

.view {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--white);
  border-radius: var(--border-radius);
  box-shadow: 0 0 4px 0 rgba(#000, 0.25);
  padding: 0 24px 16px;
  margin-top: 16px;

  &.mobile {
    display: flex;
    margin-top: 24px;

    @include media('md') {
      display: none;
    }
  }

  &.desktop {
    display: none;

    @include media('md') {
      display: flex;
    }
  }
}

.viewHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
}

.close {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 48px;
  height: 48px;
  border: 0;
  background: none;
}

.viewFilters {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @include media('sm') {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.filters,
.dateFilters,
.chartViewFilters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @include media('md') {
    flex-wrap: nowrap;
  }
}

.filters {
  width: 100%;

  @include media('sm') {
    width: max-content;
  }

  &.justifyRight {
    @include media('sm') {
      justify-content: flex-end;

      width: 100%;
    }
  }
}

.metricsFilters,
.dateFilters,
.chartViewFilters {
  padding: 6px 8px;
  border-radius: 12px;
  background: #f3f4f6;

  .filter {
    width: 100%;
    line-height: 1.2;
  }
}

.dateFilters,
.chartViewFilters {
  @include media('sm') {
    justify-content: flex-end;
  }
}

.filter {
  all: unset;
  padding: 8px 16px;
  border-radius: 8px;

  &.active,
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  &.active {
    background: var(--white);
    color: var(--purple);
    font-weight: 700;
  }

  &:hover {
    background: var(--purple);
    color: var(--white);
    cursor: pointer;
  }

  @include media('sm') {
    white-space: nowrap;
  }
}

.select {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 310px;
  height: 46px;
  background: var(--white);
  border-radius: var(--border-radius);
}

.selectHeader {
  all: unset;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid var(--gray-5);
  border-radius: var(--border-radius);

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    cursor: pointer;
  }

  .open & {
    border-bottom: 0;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  }
}

.chevron {
  color: var(--purple);
}

.selectOptionsWrap {
  position: absolute;
  left: 0;
  right: 0;
  top: 45px;
  display: none;
  overflow: hidden;
  padding: 12px 0 0 16px;
  border: 1px solid var(--gray-5);
  border-top: 0;
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  background: var(--white);

  .open & {
    display: block;
  }
}

.selectOptions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 12px 0;
  max-height: 300px;
  overflow-y: scroll;
}

.checkboxInput {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  input {
    margin-top: 6px;
    accent-color: var(--purple-dark);

    &:hover {
      accent-color: var(--purple-light);
    }
  }

  label {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: var(--purple-light);
      cursor: pointer;
    }
  }

  .option {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    span {
      overflow: hidden;
      text-wrap: wrap;
    }
  }

  .storeId {
    color: #828282;
  }

  &:has(input:checked) .storeName {
    font-weight: 600;
    color: var(--purple);
  }
}

.viewContent {
  min-height: 320px;
  max-height: 560px;
  height: 80svh;

  @include media('sm') {
    max-height: none;
    height: 640px;
  }
}

.insight {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #ffffd2;
  border: 1px solid #a0a033;
  border-radius: var(--border-radius);
  color: #4e4c16;
}

.insightCopy {
  display: flex;
  align-items: center;
  gap: 4px;

  a {
    font-weight: 700;
    text-decoration: underline;
    color: var(--purple);

    &:hover {
      color: var(--purple-light);
    }
  }
}

.lightbulb {
  min-width: 12px;
  min-height: 16px;
}

.events {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding-left: 3rem;
  padding-right: 0.75rem;

  @include media('md') {
    padding-right: 0.5rem;
  }

  @media (min-width: 1510px) {
    padding-left: 3.375rem;
    padding-right: 0.75rem;
  }
}

.eventLane {
  display: grid;
}

.event {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  color: var(--white);
  background-color: var(--gray-8);
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @include media('sm') {
    padding: 5px 8px;
    font-size: 14px;
    border-radius: 5px;
  }
}

.icon {
  display: inline-block;
  width: 24px;
  height: 24px;
  vertical-align: middle;

  @include media('sm') {
    width: 28px;
    height: 28px;
  }
}

.missingData {
  filter: blur(10px);
  pointer-events: none;
  opacity: 0.5;
}

.missingDataError {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 640px;
}

.error {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  background: var(--white);
  border-radius: var(--border-radius);
  border: 1px solid var(--gray-6);
  padding: 16px 24px;
  text-align: center;
}

.url {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  text-decoration: underline;
  color: var(--purple);

  .active & {
    color: inherit;
  }
}
</style>
